import {
  collection,
  deleteDoc,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore/lite';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';

import SkillData from '../types/skillData';
import { firestore } from '../utils/firebase';

export type NewSkillData = Omit<SkillData, 'id' | 'slug' | 'isPublished'>;

export default class SkillApi {
  private static readonly collectionReference = collection(firestore, 'skills');

  public static async getSkills(): Promise<SkillData[]> {
    const collection = await getDocs(this.collectionReference);
    const skills = collection.docs.map(doc => ({ ...doc.data(), id: doc.id } as SkillData));
    skills.sort((a, b) => a.order - b.order);
    return skills;
  }

  public static async getSkillsByAuthor(authorUuid: string): Promise<SkillData[]> {
    const querySnaps = await getDocs(
      query(this.collectionReference, where('authorUuid', '==', authorUuid), orderBy('order', 'asc'))
    );

    return querySnaps.docs.map(docSnap => {
      return { ...docSnap.data(), id: docSnap.id } as SkillData;
    });
  }

  public static async getSkillBySlug(slug: string): Promise<SkillData> {
    const q = query(this.collectionReference, where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    const doc = querySnapshot.docs[0];
    return { ...doc.data(), id: doc.id } as SkillData;
  }

  public static async getSkillsByField(fieldPath: string | FieldPath, value: unknown): Promise<SkillData[]> {
    const querySnaps = await getDocs(
      query(this.collectionReference, where(fieldPath, '==', value), orderBy('order', 'asc'))
    );
    return querySnaps.docs.map(docSnap => {
      return { ...docSnap.data(), id: docSnap.id } as SkillData;
    });
  }

  public static async getSkillById(id: string): Promise<SkillData> {
    const docRef = doc(this.collectionReference, id);
    const docSnap = await getDoc(docRef);
    return { ...docSnap.data(), id: docSnap.id } as SkillData;
  }

  public static async addSkill(newSkill: NewSkillData): Promise<string> {
    const docRef = doc(this.collectionReference);
    const slug = slugify(newSkill.name) + uuidv4().trim().substring(0, 8);
    const isPublished = false;
    await setDoc(docRef, { id: docRef.id, ...newSkill, slug, isPublished } as SkillData);
    return docRef.id;
  }

  public static async updateSkill(skill: SkillData) {
    return updateDoc(doc(this.collectionReference, skill.id), { ...skill });
  }

  public static async updateSkills(skills: SkillData[]) {
    const promises = skills.map(skill => this.updateSkill(skill));
    return Promise.all(promises);
  }

  public static async removeSkill(skillId: string): Promise<void> {
    await deleteDoc(doc(firestore, 'skills', skillId));
  }
}
